import { Injectable } from '@angular/core';
import { fillOutArray } from '../../../../functions/fill-out-array';
import { getSummarizedEmissions } from '../../../../functions/node-list/historical-emissions';
import { convertUnit } from '../../../../functions/prefixed-unit/convert-unit';
import { getBoardYearsFromTimeline } from '../../../../functions/timeline/get-board-years-from-timeline';
import { getHistoricalYearsFromTimeline } from '../../../../functions/timeline/get-historical-years-from-timeline';
import { getTransitionYearsFromTimeline } from '../../../../functions/timeline/get-transition-years-from-timeline';
import { sumArray } from '../../../../functions/utilities/array-utility';
import { lerp } from '../../../../functions/utilities/lerp';
import { BoardTimeline } from '../../../../model/board-timeline';
import { ClientNode } from '../../../../model/client-model/client-node';
import { Dict } from '../../../../model/dict';
import { WeightMetric } from '../../../../model/prefixed-unit/weight.metric';
import { STATIC_DASHBOARD_IDS } from '../../../../public/dashboard/shared/dashboard.constants';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { getNodes } from '../../functions/get-nodes';
import { CUSTOM_MODEL_CCC_ID } from '../dashboard-calculation/dashboard-calculation.service';
import {
  DashboardReportedEmissionsCalculationResult,
  DashboardV4CalculationResult,
  DashboardV4ReportedEmissionsCalculationResult,
} from '../dashboard-calculation/dashboard-v4-calculation-result';
import { ActivityGroup, ActivityTree, isActivityGroup } from '../dashboard-v4/create-activity-tree';
import { EmissionScenarioItem } from './emission-scenario-item';
import { EnergyAbatementData } from './energy-abatement-data';
import { FutureEmissionsItem } from './future-emissions-item';
import { getLeafNodeCalculationResultIds } from './get-leaf-node-calculation-result-ids';
import { MainTransitionPathwayItem } from './main-transition-pathway-item';
import { MainTransitionScenarioItem } from './main-transition-scenario-item';
import { sumEmissionDicts } from './sum-emission-dicts';
import { TransitionPathwayItem } from './transition-pathway-item';
import { TransitionScenarioItem } from './transition-scenario-item';

export interface EmissionData {
  /**
   * @deprecated used by v4.1 replaced by emissionScenarioData
   * Expected to have one item per year of the timeline
   */
  futureEmissionsData: { [year: string]: FutureEmissionsItem };
  emissionScenarioData: Dict<{ [year: string]: EmissionScenarioItem }>;
  /**
   * @deprecated used by v4.1 replaced by mainTransitionScenarioData
   * Expected to have one item per year of the timeline
   */
  mainTransitionPathway: { [year: string]: MainTransitionPathwayItem };
  /**
   * Expected to have one item per year of the timeline
   */
  mainTransitionScenarioData: { [year: string]: MainTransitionScenarioItem };
  /**
   *  @deprecated used by v4.1 replaced by transitionScenarioData
   * Indexed on node id, expected to have one item per year of the timeline.
   */
  transitionPathwayData: Dict<{ [year: string]: TransitionPathwayItem }>;
  /**
   * Indexed on node id, expected to have one item per year of the timeline.
   */
  transitionScenarioData: Dict<{ [year: string]: TransitionScenarioItem }>;
  /**
   *  @deprecated used by v4.1
   * Expected to have one item per year of the timeline
   * Indexed on node id
   */
  nodeCumulativeContributions: Dict<number>;
  /**
   *  @deprecated used by v4.1
   * Expected to have one item per year of the timeline
   * Indexed on node id
   */
  nodeCumulativeAbatements: Dict<number>;
  /**
   * Expected to have one item per year of the timeline
   * Indexed on node id
   */
  nodeEndYearContributions: Dict<number>;
  /**
   * Expected to have one item per year of the timeline
   * Indexed on node id
   */
  nodeEndYearAbatements: Dict<number>;
}

interface ReportedEmissions {
  net: number;
  gross: number;
  sink: number;
}

@Injectable({
  providedIn: 'root',
})
export class EmissionDataService {
  create(
    board: ClientBoardContainer,
    result: DashboardV4CalculationResult[],
    reportedEmissionsResult: DashboardReportedEmissionsCalculationResult,
    hideEnergyModel: boolean = false,
    activityTree: ActivityTree
  ): EmissionData {
    //Note: Net and gross emission display hasn't been completed yet. There is still some mixing of the values.
    // Emission view should be gross
    // Transition view should be net
    // Goal is set as net.

    const timeline: BoardTimeline = board.boardSettings.timeline;
    const years = getTransitionYearsFromTimeline(timeline);

    const startYearGrossEmissions = sumArray(
      Object.values(result[0].cccEmissions).map((ccc) => {
        const gross = Math.max(0, ccc.bau);
        return gross;
      })
    );

    const endYearNetReferenceEmissions = sumArray(
      Object.values(result[result.length - 1].cccEmissions).map((ccc) => ccc.bau)
    );
    const reductionGoal = board.boardSettings.reductionGoals?.[0];
    const goal: Dict<number> = {
      ...(reductionGoal?.additionalGoalsByYear ?? {}),
      ...{
        [timeline.scenarioStartYear]: 0,
        [timeline.boardEndYear]: reductionGoal?.goal ?? 0,
      },
    };
    const referenceYearEmissions = reductionGoal?.referenceYearEmissions ?? startYearGrossEmissions;
    const goalEmissions = Object.entries(goal).reduce((obj: Dict<number | undefined>, [year, goalValue]) => {
      if (year === timeline.scenarioStartYear.toString()) {
        // StartYear will get inventory emissions. We currently use gross emissions in the emission view,
        // this data will be used to create the goal series for the emission scenario diagram.
        obj[year] = startYearGrossEmissions;
      } else if (goalValue !== undefined) {
        const part = (100 - goalValue) / 100;
        obj[year] = referenceYearEmissions * part;
      }
      return obj;
    }, {});

    const referenceEmissions = result.reduce((obj, item, index) => {
      return {
        ...obj,
        [timeline.scenarioStartYear + index]: sumArray(Object.values(item.cccEmissions).map((ccc) => ccc.bau)),
      };
    }, {});

    let emissionScenarioData: Dict<{ [year: string]: EmissionScenarioItem }> = this._createEmissionScenarioData(
      result,
      reportedEmissionsResult.inventoryEmissions,
      activityTree,
      goalEmissions,
      timeline
    );
    let reportedEmissions = this._getReportedEmissionsForGroup(
      timeline,
      activityTree,
      activityTree,
      reportedEmissionsResult.inventoryEmissions
    );
    const netReportedEmissions = Object.entries(reportedEmissions).reduce(
      (dict: Dict<number | undefined>, [key, value]) => {
        dict[key] = value?.net;
        return dict;
      },
      {}
    );

    const emissions = this._createTotalEmissionData(timeline, netReportedEmissions, referenceEmissions);
    const { restrictedResult, additionalEmissions } = this._createRestrictedAbatementResult(timeline, result);
    const emissionSources = [emissions, additionalEmissions];
    let energyAbatementSeries: Array<number | undefined> | undefined;
    let restrictedEnergyAbatementSeries: Array<number | undefined> | undefined;
    let energyData: EnergyAbatementData | undefined;

    if (!hideEnergyModel) {
      const energySeries = new Array(timeline.scenarioStartYear - timeline.boardStartYear).fill(undefined).concat(
        years.map((y, t) => {
          return sumArray(
            Object.values(result[t].cccEmissions).map((ccc) => {
              return ccc.fullTransitionEnergyModelAbatement;
            })
          );
        })
      );
      const { restrictedEnergySeries, additionalEmissions } = this._createRestrictedEnergySeries(
        timeline,
        energySeries
      );
      emissionSources.push(additionalEmissions);

      const emissionSeriesIsValid = restrictedEnergySeries.some((v) => (v ?? 0) > 0);
      if (emissionSeriesIsValid) {
        energyAbatementSeries = energySeries;
        restrictedEnergyAbatementSeries = restrictedEnergySeries;

        const energyReductionSeries: number[] = [];
        result.forEach((item) => {
          Object.keys(item.cccEmissions).forEach((key) => {
            const emission = item.cccEmissions[key];
            energyReductionSeries.push(emission.onlyUtilisationTransition - emission.fullTransition);
          });
        });
        const endYearResult = result[result.length - 1];
        const endYearReductions = Object.keys(endYearResult?.cccEmissions ?? {}).map((key) => {
          const emission = endYearResult.cccEmissions[key];
          return emission.onlyUtilisationTransition - emission.fullTransition;
        });

        energyData = {
          cumulativeTotalEnergyReduction: sumArray(energyReductionSeries),
          cumulativeAbatement: sumArray(energyAbatementSeries.map((abatement) => abatement ?? 0)),
          endYearTotalEnergyReduction: sumArray(endYearReductions),
          endYearAbatement: energyAbatementSeries[energyAbatementSeries.length - 1] ?? 0,
        };
      }
    }

    const allEmissions = sumEmissionDicts(...emissionSources);
    const restrictedAbatement = this._createAbatementData(board, restrictedResult, restrictedEnergyAbatementSeries);

    const startYearNetEmissions = sumArray(
      Object.values(result[0].cccEmissions).map((ccc) => {
        return ccc.bau;
      })
    );
    const goalValues: (number | undefined)[] = [];
    years.forEach((y, t) => {
      //The interpolated values will be used to create the remaining emissions series in the transition scenario diagram.
      //That diagram uses net emissions so therefore we use the net emissions for the start year.
      const value = t === 0 ? startYearNetEmissions : goalEmissions[y];
      goalValues.push(value);
    });

    const interpolatedGoalEmissions = fillOutArray(goalValues).reduce((obj, item, index) => {
      return {
        ...obj,
        [timeline.scenarioStartYear + index]: item,
      };
    }, {});

    const mainTransitionScenarioData = this.createMainTransitionScenario(
      board,
      allEmissions,
      restrictedAbatement,
      interpolatedGoalEmissions
    );

    const transitionScenarioData = this._createTransitionScenarioData(timeline, board.nodes, restrictedAbatement);

    const abatement = this._createAbatementData(board, result, energyAbatementSeries);

    const endYearReduction: Dict<number> = Object.keys(abatement).reduce((obj: Dict<number>, nodeId) => {
      obj[nodeId] = abatement[nodeId][timeline.boardEndYear];
      return obj;
    }, {});
    const goalEndYearEmissions = goalValues[goalValues.length - 1] ?? endYearNetReferenceEmissions;
    const requiredEmissionReduction = endYearNetReferenceEmissions - goalEndYearEmissions;
    const endYear = this._createContributionData(
      requiredEmissionReduction,
      endYearReduction,
      energyData
        ? { abatement: energyData.endYearAbatement, totalEnergyReduction: energyData.endYearTotalEnergyReduction }
        : undefined
    );

    return {
      futureEmissionsData: {},
      emissionScenarioData,
      transitionPathwayData: {},
      transitionScenarioData,
      mainTransitionPathway: {},
      mainTransitionScenarioData,
      nodeCumulativeContributions: {},
      nodeCumulativeAbatements: {},
      nodeEndYearContributions: endYear.nodeContributions,
      nodeEndYearAbatements: endYear.nodeAbatements,
    };
  }

  /**
   *
   * @deprecated
   */
  createV4(
    board: ClientBoardContainer,
    result: DashboardV4CalculationResult[],
    reportedEmissionsResult: DashboardV4ReportedEmissionsCalculationResult,
    hideEnergyModel: boolean = false
  ): EmissionData {
    const timeline: BoardTimeline = board.boardSettings.timeline;
    const years = getTransitionYearsFromTimeline(timeline);

    //goal series
    const goalSeries: number[] = [];
    const reductionGoal = board.boardSettings.reductionGoals?.[0];
    if (reductionGoal) {
      const start = sumArray(
        Object.values(result[0].cccEmissions).map((ccc) => {
          return ccc.bau;
        })
      );
      const end = (reductionGoal.referenceYearEmissions * (100 - reductionGoal.goal)) / 100;
      if (years.length === 1) {
        // this is practically impossible since we validate that end years are at least one year after the start year, but still should be checked
        goalSeries.push(end);
      } else {
        years.forEach((y, t) => {
          goalSeries.push(lerp(start, end, t / (years.length - 1)));
        });
      }
    }
    const goalEmissions = goalSeries.reduce((obj, item, index) => {
      return {
        ...obj,
        [timeline.scenarioStartYear + index]: item,
      };
    }, {});
    const referenceEmissions = result.reduce((obj, item, index) => {
      return {
        ...obj,
        [timeline.scenarioStartYear + index]: sumArray(Object.values(item.cccEmissions).map((ccc) => ccc.bau)),
      };
    }, {});
    const inventoryEmissions = Object.entries(reportedEmissionsResult?.inventoryEmissions ?? {}).reduce(
      (acc, [year, histEmissions]) => {
        const numericYear = parseInt(year, 10); // Convert the year to a number
        if (numericYear >= timeline.activityBasedInventoryStartYear) {
          return {
            ...acc,
            [numericYear]: histEmissions,
          };
        }
        return acc;
      },
      {}
    );

    let historicalEmissions = this._getHistoricalEmissions(board, referenceEmissions, inventoryEmissions);

    const futureEmissionsData = this._createFutureEmissionsData(
      timeline,
      historicalEmissions,
      referenceEmissions,
      goalEmissions
    );
    let emissionScenarioData: Dict<{ [year: string]: EmissionScenarioItem }> = {};
    const emissions = this._createTotalEmissionData(timeline, historicalEmissions, referenceEmissions);
    const { restrictedResult, additionalEmissions } = this._createRestrictedAbatementResult(timeline, result);
    const emissionSources = [emissions, additionalEmissions];
    let energyAbatementSeries: Array<number | undefined> | undefined;
    let restrictedEnergyAbatementSeries: Array<number | undefined> | undefined;
    let energyData: EnergyAbatementData | undefined;

    if (!hideEnergyModel) {
      const energySeries = new Array(timeline.scenarioStartYear - timeline.boardStartYear).fill(undefined).concat(
        years.map((y, t) => {
          return sumArray(
            Object.values(result[t].cccEmissions).map((ccc) => {
              return ccc.fullTransitionEnergyModelAbatement;
            })
          );
        })
      );
      const { restrictedEnergySeries, additionalEmissions } = this._createRestrictedEnergySeries(
        timeline,
        energySeries
      );
      emissionSources.push(additionalEmissions);

      const emissionSeriesIsValid = restrictedEnergySeries.some((v) => (v ?? 0) > 0);
      if (emissionSeriesIsValid) {
        energyAbatementSeries = energySeries;
        restrictedEnergyAbatementSeries = restrictedEnergySeries;

        const energyReductionSeries: number[] = [];
        result.forEach((item) => {
          Object.keys(item.cccEmissions).forEach((key) => {
            const emission = item.cccEmissions[key];
            energyReductionSeries.push(emission.onlyUtilisationTransition - emission.fullTransition);
          });
        });

        energyData = {
          cumulativeTotalEnergyReduction: sumArray(energyReductionSeries),
          cumulativeAbatement: sumArray(energyAbatementSeries.map((abatement) => abatement ?? 0)),
          endYearTotalEnergyReduction: energyReductionSeries[energyReductionSeries.length - 1] ?? 0,
          endYearAbatement: energyAbatementSeries[energyAbatementSeries.length - 1] ?? 0,
        };
      }
    }

    const allEmissions = sumEmissionDicts(...emissionSources);
    const restrictedAbatement = this._createAbatementData(board, restrictedResult, restrictedEnergyAbatementSeries);

    const mainTransitionPathway = this._createMainTransitionPathway(
      board,
      allEmissions,
      restrictedAbatement,
      goalEmissions
    );

    const mainTransitionScenarioData = this.createMainTransitionScenario(
      board,
      allEmissions,
      restrictedAbatement,
      goalEmissions
    );

    const transitionPathwayData = this._createTransitionPathwayData(
      timeline,
      board.nodes,
      allEmissions,
      restrictedAbatement,
      mainTransitionPathway
    );

    const transitionScenarioData = this._createTransitionScenarioData(timeline, board.nodes, restrictedAbatement);

    const abatement = this._createAbatementData(board, result, energyAbatementSeries);

    const accumulatedEmissionReduction: Dict<number> = Object.keys(abatement).reduce((obj: Dict<number>, nodeId) => {
      obj[nodeId] = this._getAccumulatedEmissionReduction(abatement[nodeId], timeline);
      return obj;
    }, {});
    const cumulative = this._createContributionData(
      this._getAccumulatedRequiredEmissionReduction(futureEmissionsData, timeline),
      accumulatedEmissionReduction,
      energyData
        ? { abatement: energyData.cumulativeAbatement, totalEnergyReduction: energyData.cumulativeTotalEnergyReduction }
        : undefined
    );

    const endYearReduction: Dict<number> = Object.keys(abatement).reduce((obj: Dict<number>, nodeId) => {
      obj[nodeId] = abatement[nodeId][timeline.boardEndYear];
      return obj;
    }, {});

    const endYear = this._createContributionData(
      this._getRequiredEmissionReduction(futureEmissionsData[timeline.boardEndYear]),
      endYearReduction,
      energyData
        ? { abatement: energyData.endYearAbatement, totalEnergyReduction: energyData.endYearTotalEnergyReduction }
        : undefined
    );

    return {
      futureEmissionsData,
      emissionScenarioData,
      transitionPathwayData,
      transitionScenarioData,
      mainTransitionPathway,
      mainTransitionScenarioData,
      nodeCumulativeContributions: cumulative.nodeContributions,
      nodeCumulativeAbatements: cumulative.nodeAbatements,
      nodeEndYearContributions: endYear.nodeContributions,
      nodeEndYearAbatements: endYear.nodeAbatements,
    };
  }

  private _createAbatementData(
    board: ClientBoardContainer,
    results: DashboardV4CalculationResult[],
    energyAbatementSeries?: Array<number | undefined>
  ): Dict<{ [year: string]: number }> {
    const timeline = board.boardSettings.timeline;

    const abatement: Dict<{ [year: string]: number }> = Object.entries(board.nodes).reduce(
      (obj: Dict<any>, [nodeId, node]) => {
        const leafNodeIds = getLeafNodeCalculationResultIds(node, board);
        const abatement = this._createAbatementForNode(leafNodeIds, results, timeline);
        obj[nodeId] = abatement;
        return obj;
      },
      {}
    );
    abatement[board.id] = this._createAbatementForNode(
      getLeafNodeCalculationResultIds(board, board),
      results,
      timeline
    );
    if (energyAbatementSeries) {
      const allYears = getBoardYearsFromTimeline(timeline);

      abatement[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID] = allYears.reduce(
        (abatements: { [year: string]: number }, year, t) => {
          if (year >= timeline.scenarioStartYear) {
            //we are in the transition timeline so we should not have any undefined values
            abatements[year] = energyAbatementSeries[t] ?? 0;
          }
          return abatements;
        },
        {}
      );
    }

    return abatement;
  }

  private _createAbatementForNode(
    teIds: string[],
    results: DashboardV4CalculationResult[],
    timeline: BoardTimeline
  ): { [year: string]: number } {
    const years = getTransitionYearsFromTimeline(timeline);
    return years.reduce((abatement: { [year: string]: number }, year, t) => {
      const result = results[t];
      const abatementForYear = teIds.reduce((sum, id) => {
        const abatement = result.teCarbonAbatement[id]?.fullTransition ?? 0;
        return sum + abatement;
      }, 0);
      abatement[year] = abatementForYear;
      return abatement;
    }, {});
  }

  private _createContributionData(
    requiredEmissionReduction: number,
    emissionReduction: Dict<number>,
    energyData?: { totalEnergyReduction: number; abatement: number }
  ): { nodeContributions: Dict<number>; nodeAbatements: Dict<number> } {
    const nodeContributions: Dict<number> = {};
    const nodeAbatements: Dict<number> = {};

    Object.entries(emissionReduction).forEach(([nodeId, emissionReduction]) => {
      nodeAbatements[nodeId] = emissionReduction;
      nodeContributions[nodeId] = (emissionReduction / requiredEmissionReduction) * 100;
    });

    //TODO: mod-208: This should not be needed when energy has a proper child node.
    if (energyData) {
      nodeContributions[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID] =
        (energyData.abatement / requiredEmissionReduction) * 100;
      nodeContributions[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_FULL_ID] =
        (energyData.totalEnergyReduction / requiredEmissionReduction) * 100;
      nodeAbatements[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID] = energyData.abatement;
    }
    return { nodeContributions, nodeAbatements };
  }

  private _createEmissionScenarioData(
    result: DashboardV4CalculationResult[],
    inventoryEmissions: Dict<Dict<number>>,
    activityTree: ActivityTree,
    mainGoalEmissions: { [year: string]: number | undefined },
    timeline: BoardTimeline
  ): Dict<{ [year: string]: EmissionScenarioItem }> {
    const years = getBoardYearsFromTimeline(timeline);
    const scenarioStartYear = timeline.scenarioStartYear;
    const activityGroups = activityTree.groups;

    const groups = [...Object.values(activityGroups), activityTree];
    const groupReportedEmissions: Dict<Dict<ReportedEmissions | undefined>> = groups.reduce(
      (groupEmissions: Dict<Dict<ReportedEmissions | undefined>>, group) => {
        groupEmissions[group.id] = this._getReportedEmissionsForGroup(
          timeline,
          group,
          activityTree,
          inventoryEmissions
        );
        return groupEmissions;
      },
      {}
    );
    const transitionOffset = scenarioStartYear - timeline.boardStartYear;

    const buildItem = (group: ActivityGroup | ActivityTree, goalSeries: { [year: string]: number | undefined }) => {
      const activities = this.getActivities(activityGroups, group);
      const scenarioIds = group.children.length > 0 ? group.children : [group.id];
      const scenarioActivities =
        group.children.length > 0
          ? group.children.map((id) => this.getActivities(activityGroups, activityGroups[id]))
          : isActivityGroup(group)
            ? [group.activityIds ?? []]
            : [[]];
      const emissionItems: { [year: string]: EmissionScenarioItem } = {};

      years.forEach((year, t) => {
        const resultForYear = result[t - transitionOffset];
        const childEmissions = scenarioActivities.reduce((dict: Dict<number | undefined>, activities, index) => {
          const scenarioEmissions = resultForYear
            ? activities.reduce((sum, activity) => {
                const emissions = resultForYear.cccEmissions[activity]?.fullTransition ?? 0;
                // limiting to positive since negative is collected as sinks.
                return sum + Math.max(emissions, 0);
              }, 0)
            : undefined;
          dict[scenarioIds[index]] = scenarioEmissions;
          return dict;
        }, {});
        const reportedEmissionList = group.children.length > 0 ? group.children : [group.id];
        const childReportedEmissions = reportedEmissionList.reduce((reported: Dict<number | undefined>, id) => {
          const childReported = groupReportedEmissions[id];
          if (childReported === undefined) {
            return reported;
          }
          reported[id] = childReported[year]?.gross;
          return reported;
        }, {});
        const childScenarioEmissions = year < scenarioStartYear ? childReportedEmissions : childEmissions;
        const referenceEmissions = resultForYear
          ? activities.reduce((sum, activity) => {
              const grossEmissions = Math.max(0, resultForYear.cccEmissions[activity]?.bau ?? 0);
              return sum + grossEmissions;
            }, 0)
          : undefined;
        let reportedForGroup: number | undefined = groupReportedEmissions[group.id][year]?.gross ?? 0;
        if (reportedForGroup === 0) {
          //We'll handle 0 reported as unset for now.
          reportedForGroup = undefined;
        }
        emissionItems[year] = {
          reportedEmissions: reportedForGroup,
          referenceEmissions,
          goalEmissions: goalSeries[year],
          scenarioEmissions: Object.values(childEmissions).reduce((sum: number | undefined, emission) => {
            if (sum === undefined && emission === undefined) {
              return undefined;
            }
            return (sum ?? 0) + (emission ?? 0);
          }, undefined),
          childScenarioEmissions,
        };
      });
      return emissionItems;
    };
    const emissionItems = Object.values(activityGroups).reduce(
      (emissionScenarioData: Dict<{ [year: string]: EmissionScenarioItem }>, group) => {
        //until we have the possibility to set a goal for activity groups, we will not generate any goal emissions.
        emissionScenarioData[group.id] = buildItem(group, {});
        return emissionScenarioData;
      },
      {}
    );
    const root = buildItem(activityTree, mainGoalEmissions);
    const allActivities = [...this.getActivities(activityGroups, activityTree), CUSTOM_MODEL_CCC_ID];
    const sinks: Dict<number | undefined> = {};
    Object.entries(groupReportedEmissions[activityTree.id]).forEach(([year, r]) => {
      sinks[year] = r?.sink;
    });
    result.forEach((r, i) => {
      const total = allActivities.reduce((sum, activity) => {
        const sink = Math.min(r.cccEmissions[activity]?.fullTransition ?? 0, 0);
        return sum + sink;
      }, 0);
      const year = scenarioStartYear + i;
      sinks[year] = total;
    });

    const allSinks = Object.values(sinks).reduce((sum: number, sink) => sum + (sink ?? 0), 0);
    if (allSinks < 0) {
      years.forEach((year) => {
        const sink = sinks[year];
        root[year].childScenarioEmissions[STATIC_DASHBOARD_IDS.EMISSION_SINK_ID] = sink;
      });
    }
    emissionItems[activityTree.id] = root;
    return emissionItems;
  }

  private _getReportedEmissionsForGroup(
    timeline: BoardTimeline,
    group: ActivityGroup | ActivityTree,
    activityTree: ActivityTree,
    inventoryEmissions: Dict<Dict<number>>
  ): Dict<ReportedEmissions | undefined> {
    const yearlyEmissions: Dict<ReportedEmissions | undefined> = {};
    const reportedEmissions = group.reportedEmissions;
    Object.keys(reportedEmissions).forEach((year) => {
      const entry = reportedEmissions[year];
      const reported = yearlyEmissions[year] ?? { net: 0, gross: 0, sink: 0 };
      reported.net += entry;
      reported.gross += entry;
      yearlyEmissions[year] = reported;
    });
    const activities = this.getActivities(activityTree.groups, group);
    Object.keys(inventoryEmissions).forEach((year) => {
      const inventoryEmissionsForYear = inventoryEmissions[year];
      let net = 0;
      let gross = 0;
      let sink = 0;
      activities.forEach((activity) => {
        const emissions = inventoryEmissionsForYear[activity];
        net += emissions ?? 0;
        gross += Math.max(0, emissions ?? 0);
        sink += Math.min(0, emissions ?? 0);
      });
      const nothingReported = net === 0 && gross === 0 && sink === 0;
      // We can't differentiate between an inventory with nothing set and an inventory with 0 values so will set 0 as unset.
      // The exception is the first year, there we assume that the inventory should be always set, even if it is 0.
      // This is important because fore some calculations we need to use interpolated values and if the first year is unset, but one of the following is then the data will be incorrect.
      const shouldBeUnset = nothingReported && year !== timeline.activityBasedInventoryStartYear.toString();
      yearlyEmissions[year] = shouldBeUnset ? undefined : { net, gross, sink };
    });
    return yearlyEmissions;
  }

  private _createTotalEmissionData(
    timeline: BoardTimeline,
    reportedEmissions: { [year: string]: number | undefined },
    referenceEmissions: { [year: string]: number }
  ): { [year: string]: number } {
    const allYears = getBoardYearsFromTimeline(timeline);
    //filling out to ensure that we have a value for each year
    const historicalEmissionSeries: number[] = fillOutArray(
      allYears.map((y) => {
        return reportedEmissions[y];
      })
    );
    return allYears.reduce((emissions: { [year: string]: number }, year, index) => {
      const isHistorical = year < timeline.scenarioStartYear;
      emissions[year] = isHistorical ? historicalEmissionSeries[index] : referenceEmissions[year];
      return emissions;
    }, {});
  }

  /**
   * @deprecated used by v4.1 replaced by _createEmissionScenarioData
   */
  private _createFutureEmissionsData(
    timeline: BoardTimeline,
    historicalEmissions: { [year: string]: number | undefined },
    referenceEmissions: { [year: string]: number },
    goalEmissions: { [year: string]: number }
  ): { [year: string]: FutureEmissionsItem } {
    const years = getBoardYearsFromTimeline(timeline);
    return years.reduce((data: { [year: string]: FutureEmissionsItem }, year) => {
      const historicalEmissionValue = historicalEmissions[year];
      data[year] = {
        historicalEmissions: historicalEmissionValue,
        referenceEmissions: referenceEmissions[year],
        goalEmissions: goalEmissions[year],
      };
      return data;
    }, {});
  }

  /**
   * @deprecated used by 4.1. Replaced by createMainTransitionScenarioData
   */
  private _createMainTransitionPathway(
    board: ClientBoardContainer,
    emissions: { [year: string]: number | undefined },
    abatementData: Dict<{ [year: string]: number }>,
    goalEmissions: { [year: string]: number }
  ): { [year: string]: MainTransitionPathwayItem } {
    const timeline = board.boardSettings.timeline;
    const allYears = getBoardYearsFromTimeline(timeline);
    const boardAbatementData = abatementData[board.id];
    return allYears.reduce((pathway: { [year: string]: MainTransitionPathwayItem }, year) => {
      const abatement: { id: string; value: number | undefined }[] = board.childNodes.map((nodeId) => {
        return { id: nodeId, value: abatementData[nodeId][year] };
      });
      const energyAbatement = abatementData[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID]?.[year];
      if (abatementData.hasOwnProperty(STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID)) {
        abatement.push({
          id: STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID,
          value: energyAbatement,
        });
      }
      const totalEmissions = emissions[year] ?? 0;
      if (year < timeline.scenarioStartYear) {
        pathway[year] = {
          abatement,
          remainingEmissions: totalEmissions,
          gap: undefined,
        };
        return pathway;
      }
      const totalUtilisationAbatement = boardAbatementData[year];
      const totalAbatement = totalUtilisationAbatement + (energyAbatement ?? 0);
      const goalAbatement = totalEmissions - (goalEmissions[year] ?? 0);
      const remainingEmissions = totalEmissions - Math.max(goalAbatement, totalAbatement);
      const gap = goalAbatement - totalAbatement;
      pathway[year] = {
        abatement,
        remainingEmissions,
        gap,
      };
      return pathway;
    }, {});
  }

  createMainTransitionScenario(
    board: ClientBoardContainer,
    emissions: { [year: string]: number | undefined },
    abatementData: Dict<{ [year: string]: number }>,
    goalEmissions: { [year: string]: number }
  ): { [year: string]: MainTransitionScenarioItem } {
    const timeline = board.boardSettings.timeline;
    const allYears = getBoardYearsFromTimeline(timeline);
    const boardAbatementData = abatementData[board.id];
    return allYears.reduce((scenario: { [year: string]: MainTransitionScenarioItem }, year) => {
      const abatement: { id: string; value: number | undefined }[] = board.childNodes.map((nodeId) => {
        return { id: nodeId, value: abatementData[nodeId][year] };
      });
      const energyAbatement = abatementData[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID]?.[year];
      if (abatementData.hasOwnProperty(STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID)) {
        abatement.push({
          id: STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID,
          value: energyAbatement,
        });
      }
      const totalEmissions = emissions[year] ?? 0;
      if (year < timeline.scenarioStartYear) {
        scenario[year] = {
          abatement,
          remainingEmissions: totalEmissions,
          gap: undefined,
          goalEmissions: undefined,
        };
        return scenario;
      }
      const totalUtilisationAbatement = boardAbatementData[year];
      const totalAbatement = totalUtilisationAbatement + (energyAbatement ?? 0);
      const goalAbatement = totalEmissions - (goalEmissions[year] ?? 0);
      const remainingEmissions = totalEmissions - Math.max(goalAbatement, totalAbatement);
      const gap = goalAbatement - totalAbatement;
      scenario[year] = {
        abatement,
        remainingEmissions,
        gap,
        goalEmissions: goalEmissions[year],
      };
      return scenario;
    }, {});
  }

  /**
   * Restricts the abatement, ONLY fullTransition, to not be negative, any negative abatement will be returned as additional emissions.
   */
  private _createRestrictedAbatementResult(
    timeline: BoardTimeline,
    results: DashboardV4CalculationResult[]
  ): {
    restrictedResult: DashboardV4CalculationResult[];
    additionalEmissions: { [year: string]: number };
  } {
    const restrictedResult: DashboardV4CalculationResult[] = [];
    const additionalEmissions: { [year: string]: number } = {};
    const transitionYears = getTransitionYearsFromTimeline(timeline);
    transitionYears.forEach((year, t) => {
      const result = results[t];
      let emissions = 0;
      const restrictedAbatement: Dict<{ fullTransition: number; onlyUtilisation: number }> = {};
      Object.entries(result.teCarbonAbatement).forEach(([id, abatement]) => {
        const abatementValue = abatement.fullTransition ?? 0;
        emissions += abatementValue < 0 ? Math.abs(abatementValue) : 0;
        restrictedAbatement[id] = {
          ...abatement,
          fullTransition: Math.max(0, abatementValue),
        };
      });

      restrictedResult.push({
        ...result,
        teCarbonAbatement: restrictedAbatement,
      });
      additionalEmissions[year] = emissions;
    });
    return { restrictedResult, additionalEmissions };
  }

  private _createRestrictedEnergySeries(
    timeline: BoardTimeline,
    energySeries: (number | undefined)[]
  ): { restrictedEnergySeries: (number | undefined)[]; additionalEmissions: { [year: string]: number } } {
    const additionalEmissions: { [year: string]: number } = {};
    const restrictedEnergySeries: (number | undefined)[] = [];
    const allYears = getBoardYearsFromTimeline(timeline);
    allYears.forEach((year, t) => {
      let value = energySeries[t];
      if (year >= timeline.scenarioStartYear) {
        additionalEmissions[year] = 0;
        if (value && value < 0) {
          additionalEmissions[year] = Math.abs(value);
          value = 0;
        }
      }
      restrictedEnergySeries.push(value);
    });
    return { restrictedEnergySeries, additionalEmissions };
  }

  /**
   * @deprecated used by 4.1. Replaced by createTransitionScenarioData
   */
  private _createTransitionPathwayData(
    timeline: BoardTimeline,
    nodes: Dict<ClientNode>,
    emissions: { [year: string]: number | undefined },
    abatementData: Dict<{ [year: string]: number }>,
    mainTransitionPathway: { [year: string]: MainTransitionPathwayItem }
  ): Dict<{ [year: string]: TransitionPathwayItem }> {
    const allYears = getBoardYearsFromTimeline(timeline);

    return Object.keys(nodes).reduce(
      (transitionPathwayData: Dict<{ [year: string]: TransitionPathwayItem }>, nodeId) => {
        const nodeData: { [year: string]: TransitionPathwayItem } = {};

        allYears.forEach((year) => {
          const totalEmissions = emissions[year] ?? 0;
          if (year < timeline.scenarioStartYear) {
            nodeData[year] = {
              abatement: undefined,
              otherAreas: undefined,
              remainingEmissions: totalEmissions,
            };
            return nodeData;
          }

          const remainingEmissions = mainTransitionPathway[year].remainingEmissions ?? 0;
          const abatement = abatementData[nodeId][year];
          nodeData[year] = {
            abatement,
            otherAreas: totalEmissions - remainingEmissions - abatement,
            remainingEmissions,
          };
        });
        transitionPathwayData[nodeId] = nodeData;
        return transitionPathwayData;
      },
      {}
    );
  }

  private _createTransitionScenarioData(
    timeline: BoardTimeline,
    nodes: Dict<ClientNode>,
    abatementData: Dict<{ [year: string]: number }>
  ): Dict<{ [year: string]: TransitionScenarioItem }> {
    const transitionYears = getTransitionYearsFromTimeline(timeline);

    return Object.keys(nodes).reduce((scenarioData: Dict<{ [year: string]: TransitionScenarioItem }>, nodeId) => {
      const nodeData: { [year: string]: TransitionScenarioItem } = {};
      const seriesNodes = nodes[nodeId].childNodes.length > 0 ? nodes[nodeId].childNodes : [nodeId];

      transitionYears.forEach((year) => {
        nodeData[year] = {
          abatement: seriesNodes.map((childId) => ({ id: childId, value: abatementData[childId][year] })),
        };
      });
      scenarioData[nodeId] = nodeData;
      return scenarioData;
    }, {});
  }

  private _getAccumulatedEmissionReduction(abatement: { [year: string]: number }, timeline: BoardTimeline): number {
    return Object.keys(abatement)
      .filter((y) => Number(y) >= timeline.scenarioStartYear && Number(y) <= timeline.boardEndYear)
      .reduce((sum, year) => {
        return sum + (abatement[year] ?? 0);
      }, 0);
  }
  /**
   *
   * @deprecated used by 4.1.
   */
  private _getAccumulatedRequiredEmissionReduction(
    futureEmissionsData: { [year: string]: FutureEmissionsItem },
    timeline: BoardTimeline
  ): number {
    return Object.keys(futureEmissionsData)
      .filter((y) => Number(y) >= timeline.scenarioStartYear && Number(y) <= timeline.boardEndYear)
      .reduce((sum, year) => {
        const item: FutureEmissionsItem = futureEmissionsData[year];
        return sum + this._getRequiredEmissionReduction(item);
      }, 0);
  }

  private getActivities(activityGroups: Dict<ActivityGroup>, group: ActivityGroup | ActivityTree): string[] {
    if (isActivityGroup(group) && group.activityIds) {
      return group.activityIds;
    }
    return group.children.flatMap((childId) => {
      return this.getActivities(activityGroups, activityGroups[childId]);
    });
  }
  /**
   *
   * @deprecated used by 4.1.
   */
  private _getHistoricalEmissions(
    board: ClientBoardContainer,
    referenceEmissions: { [year: string]: number | undefined },
    inventoryEmissions: { [year: string]: number | undefined }
  ): { [year: string]: number | undefined } {
    const timeline = board.boardSettings.timeline;
    const historicalYears = getHistoricalYearsFromTimeline(timeline);
    const boardWeightMetric = board.boardSettings.displayUnit;
    const summarizedEmissions = getSummarizedEmissions({
      allNodes: getNodes(board),
      node: { childNodes: board.childNodes } as ClientNode,
      timeline,
    });
    return historicalYears.reduce((emissions: { [year: string]: number | undefined }, year) => {
      const value = summarizedEmissions[year];
      if (year === timeline.scenarioStartYear) {
        //reference emissions from scenarioStartYear is from the inventory so we use that as reported(historical) emissions.
        emissions[year] = referenceEmissions[year] ?? 0;
      } else if (year >= timeline.activityBasedInventoryStartYear && year <= timeline.latestFinalisedInventoryYear) {
        if (inventoryEmissions[year] !== 0) {
          emissions[year] = inventoryEmissions[year];
        }
      } else {
        emissions[year] = value ? convertUnit(boardWeightMetric, WeightMetric.Gram, value) : undefined;
      }
      return emissions;
    }, {});
  }
  /**
   *
   * @deprecated used by 4.1.
   */
  private _getRequiredEmissionReduction(item: FutureEmissionsItem): number {
    return (item.referenceEmissions ?? 0) - (item.goalEmissions ?? 0);
  }
}
